import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { log } from '../../utils/environment';

class EnhancedErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      componentStack: ''
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      errorInfo: errorInfo,
      componentStack: errorInfo.componentStack
    });
    
    // Log error with context
    log.error('ErrorBoundary caught an error:', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      component: this.props.componentName || 'Unknown',
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });
    
    // Send to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      this.reportError(error, errorInfo);
    }
  }

  reportError = (error, errorInfo) => {
    // In a real app, send to Sentry, LogRocket, etc.
    console.log('Error reported:', {
      error,
      errorInfo,
      component: this.props.componentName
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      componentStack: '' 
    });
  };

  render() {
    if (this.state.hasError) {
      const { componentName = 'Component', fallback: FallbackComponent } = this.props;
      
      // Use custom fallback if provided
      if (FallbackComponent) {
        return <FallbackComponent error={this.state.error} resetError={this.handleReset} />;
      }
      
      return (
        <div className="error-boundary" style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>⚠️</div>
            <h2 style={styles.title}>Something went wrong</h2>
            <p style={styles.message}>
              {this.props.errorMessage || `There was an error loading ${componentName}.`}
            </p>
            
            <div style={styles.actions}>
              <button 
                onClick={this.handleReset}
                style={styles.button.primary}
              >
                Try Again
              </button>
              <button 
                onClick={this.handleReload}
                style={styles.button.secondary}
              >
                Reload Page
              </button>
              <button 
                onClick={this.handleGoHome}
                style={styles.button.secondary}
              >
                Go to Homepage
              </button>
            </div>
            
            {this.props.showDetails && (
              <details style={styles.details}>
                <summary style={styles.summary}>Error Details</summary>
                <div style={styles.errorContent}>
                  <h4>Error:</h4>
                  <pre style={styles.pre}>
                    {this.state.error?.toString()}
                  </pre>
                  
                  <h4 style={{ marginTop: '20px' }}>Component Stack:</h4>
                  <pre style={styles.pre}>
                    {this.state.componentStack}
                  </pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <h4 style={{ marginTop: '20px' }}>Error Info:</h4>
                      <pre style={styles.pre}>
                        {JSON.stringify(this.state.errorInfo, null, 2)}
                      </pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

EnhancedErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  componentName: PropTypes.string,
  errorMessage: PropTypes.string,
  fallback: PropTypes.elementType,
  showDetails: PropTypes.bool,
};

EnhancedErrorBoundary.defaultProps = {
  componentName: 'Component',
  errorMessage: '',
  showDetails: process.env.NODE_ENV === 'development',
};

const styles = {
  container: {
    display: 'flex',
    minHeight: '400px',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8f9fa',
    padding: '40px 20px',
  },
  content: {
    maxWidth: '600px',
    textAlign: 'center',
    backgroundColor: 'white',
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  },
  icon: {
    fontSize: '60px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1a365d',
    marginBottom: '10px',
  },
  message: {
    fontSize: '16px',
    color: '#718096',
    marginBottom: '30px',
    lineHeight: '1.6',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
    marginBottom: '30px',
  },
  button: {
    primary: {
      padding: '12px 24px',
      backgroundColor: '#1ca29b',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    secondary: {
      padding: '12px 24px',
      backgroundColor: '#f8fafc',
      color: '#475569',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
  },
  details: {
    marginTop: '20px',
    textAlign: 'left',
  },
  summary: {
    cursor: 'pointer',
    padding: '10px 15px',
    backgroundColor: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#4a5568',
  },
  errorContent: {
    marginTop: '15px',
    padding: '15px',
    backgroundColor: '#1a202c',
    borderRadius: '6px',
    color: '#cbd5e0',
  },
  pre: {
    margin: '10px 0',
    padding: '10px',
    backgroundColor: '#2d3748',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '12px',
    lineHeight: '1.4',
  },
};

export default EnhancedErrorBoundary;

// HOC for wrapping components with error boundaries
export const withErrorBoundary = (WrappedComponent, options = {}) => {
  const WithErrorBoundary = (props) => (
    <EnhancedErrorBoundary {...options}>
      <WrappedComponent {...props} />
    </EnhancedErrorBoundary>
  );
  
  WithErrorBoundary.displayName = `WithErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  
  return WithErrorBoundary;
};